import classNames from 'classnames';
import React, {
  ButtonHTMLAttributes,
  ForwardedRef,
  PropsWithChildren,
  forwardRef,
} from 'react';
import LoadingCircle from '../loading/LoadingCircle';

type ButtonFill = 'solid' | 'outline' | 'clear' | 'gradient';

type ButtonColor = 'primary' | 'danger' | 'info' | 'disabled';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  fill?: ButtonFill;
  color?: ButtonColor;
  loading?: boolean;
  iconRight?: React.ReactNode;
  children: PropsWithChildren;
};

const theme: Record<ButtonFill, Record<ButtonColor, string>> = {
  solid: {
    primary:
      'bg-primary text-black transition-all active:bg-primary-dark disabled:bg-primary-dark disabled:opacity-50 hover:opacity-80',
    danger:
      'bg-red-700 text-white transition-all hover:bg-red-900 active:bg-red-900 disabled:bg-red-900 disabled:bg-opacity-70',
    info: 'bg-blue-700 text-white transition-all hover:bg-blue-500 active:bg-blue-900 disabled:bg-blue-900 disabled:bg-opacity-70',
    disabled: 'bg-gray-500 text-white transition-all    cursor-not-allowed',
  },
  outline: {
    primary:
      'border border-primary bg-transparent text-primary transition-all hover:bg-white/10 hover:bg-opacity-5 active:bg-primary-light active:bg-opacity-10 disabled:bg-primary-dark disabled:bg-opacity-10',
    danger:
      'border border-red-700 bg-transparent text-red-700 transition-all hover:bg-red-900 hover:bg-opacity-5 active:bg-red-300 active:bg-opacity-10 disabled:bg-red-900 disabled:bg-opacity-10',
    info: 'border border-blue-500 bg-transparent text-blue-500 transition-all hover:bg-blue-700 hover:bg-opacity-5 active:bg-blue-300 active:bg-opacity-10 disabled:bg-blue-900 disabled:bg-opacity-10  cursor-not-allowed',
    disabled: 'bg-gray-500 text-white transition-all ',
  },
  clear: {
    primary:
      'bg-transparent text-primary transition-all hover:text-primary-dark active:text-primary-dark disabled:bg-grey-800',
    danger:
      'bg-transparent text-red-700 transition-all hover:text-red-900 active:text-red-900 disabled:bg-grey-800',
    info: 'bg-transparent text-blue-500 transition-all hover:text-blue-700 active:text-blue-700 disabled:bg-blue-800',
    disabled: 'text-white transition-all   cursor-not-allowed',
  },
  gradient: {
    primary:
      'bg-gradient-to-r from-primary-400 via-primary to-primary-600 text-black transition-all hover:from-primary-600 hover:to-primary-700 active:bg-primary-200',
    danger:
      'bg-gradient-to-r from-red-700 to-red-900 text-white transition-all hover:from-red-800 hover:to-red-900 ',
    info: 'bg-gradient-to-r from-blue-700 to-blue-500 text-white transition-all hover:bg-blue-500 active:bg-blue-900 disabled:bg-blue-900 disabled:bg-opacity-70',
    disabled: 'bg-gray-700 text-white transition-all    cursor-not-allowed',
  },
};

function Button(
  {
    fill = 'solid',
    color = 'primary',
    loading = false,
    className,
    children,
    iconRight,
    ...props
  }: ButtonProps,

  ref: ForwardedRef<HTMLButtonElement>
) {
  const base = 'w-full rounded py-3 px-8 flex flex-row justify-center';

  const handleColor = props.disabled && !loading ? 'disabled' : color;
  const loadingColor = fill === 'outline' ? 'primary' : 'black';

  const classes = classNames(base, theme[fill][handleColor], className);
  return (
    <button className={classes} ref={ref} {...props}>
      {loading === true ? (
        <div className="flex flex-row w-full justify-center gap-5 items-center">
          {children} <LoadingCircle color={loadingColor} />
        </div>
      ) : (
        children
      )}
      {iconRight}
    </button>
  );
}

export type { ButtonColor, ButtonFill, ButtonProps };

export default forwardRef(Button);
